
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import travelRepository from '../repositories/travel_repository';
import { Trip, Program, TripDetail, AdminProgramsOverview, Department, AdminTripsOverview, TravelProfile, Traveler, Country, ExportItem, Role, Segment, Transport, Airline, Airport, City, AdminTrips, Flight, ContactList, TravelerContact, Contact, AdminProgramStats, AdminTripStats, State, ProgramContact } from '../api/TravelApi';


export interface SearchFilters {
    searchTerm?: string;
    tripTypes?: string;
    countryIds?: string;
    cityIds?: string;
    departmentIds?: string;
    statuses?: string;
    dates?: string;
    isHighRiskOnly?: boolean;
    isFinalDestination?: boolean;
    affiliations?: string;
}

export interface TravelerProfile {
    mitId: string;
    invitedByPersonMitId?: string;
    invitedOn?: string;
    lastUpdated?: string;
}

// Define and export the store
export const useTravelStore = defineStore('travel', () => {
    const programs = ref<Program[]>([]);
    const programsOverview = ref<AdminProgramsOverview>();
    const tripsOverview = ref<AdminTripsOverview>();
    const trips = ref<Trip[]>([]);
    const selectedProgram = ref<Program | null>(null);
    const programStats = ref<AdminProgramStats | null>(null);
    const selectedProgramDetails = ref<TripDetail[]>([]);
    const selectedTrip = ref<Trip | null>(null);
    const tripStats = ref<AdminTripStats | null>(null);
    const departments = ref<Department[]>([]);

    const highRiskCountries = ref<Country[]>([]);
    const pendingLegKeys = ref<string[]>([]);
    const optimisticNewLegs = ref<Segment[]>([]);
    const allCountries = ref<Country[]>([]);
    const states = ref<State[]>([]);
    const searchFilters = ref<SearchFilters>({});
    const selectedTravelerProfile = ref<TravelerProfile | null>(null);
    const isTripsInitialised = ref(false);
    const adminRole = ref<Role | null>(null);
    const transportModes = ref<Transport[]>([]);
    const selectedAdminTrip = ref<AdminTrips | null>(null);

    // AbortController for cancelling ongoing fetchPrograms requests
    let fetchProgramsAbortController: AbortController | null = null;

    // AbortController for cancelling ongoing fetchTrips requests
    let fetchTripsAbortController: AbortController | null = null;

    // AbortController for cancelling ongoing fetchTrips requests for a traveler
    let fetchTravelerTripsAbortController: AbortController | null = null;

    const hasAccess = computed(() =>
        adminRole.value != null && (adminRole.value.superAdmin || adminRole.value.admin));


    function delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async function fetchRole() {
        const response = await travelRepository.roles.rolesList();
        adminRole.value = response.data;
        fetchDepartments();
    }

    async function fetchTravelerProfile(mitId: string): Promise<TravelProfile | undefined> {
        try {
            const response = await travelRepository.travelProfile.travelProfileDetail(mitId);
            return response.data;
        } catch (error) {
            console.error('Error fetching traveler profile:', error);
            return undefined
        }
    }

    async function fetchTripTravelerContact(tripId: string): Promise<TravelerContact | undefined> {
        try {
            const response = await travelRepository.contacts.contactsList({ contactType: 'self', tripId: tripId });
            const list: ContactList = response.data ?? [];
            return list.contacts && list.contacts.length > 0 ? (list.contacts[0] as TravelerContact) : undefined;
        } catch (error) {
            console.error('Error fetching trip traveler contact:', error);
        }
    }

    async function fetchTripEmergencyContact(tripId: string): Promise<Contact | undefined> {
        try {
            const response = await travelRepository.contacts.contactsList({ contactType: 'emergency', tripId: tripId });
            const list: ContactList = response.data ?? [];
            return list.contacts && list.contacts.length > 0 ? (list.contacts[0] as Contact) : undefined;
        } catch (error) {
            console.error('Error fetching trip emergency contact:', error);
        }
    }

    async function fetchPrograms(
        pageNumber: number = 1,
        sortField: string = "DATES",
        sortOrder: string = "desc",
    ): Promise<void> {
        try {
            // Cancel the previous request if it's still ongoing
            if (fetchProgramsAbortController) {
                fetchProgramsAbortController.abort();
                console.log('Previous fetchPrograms request cancelled');
            }

            // Create a new AbortController for this request
            fetchProgramsAbortController = new AbortController();

            // Run both API calls in parallel
            const [programsResponse, statsResponse] = await Promise.all([
                travelRepository.admin.programsList({
                    pageNumber: pageNumber.toString(),
                    pageSize: "20",
                    sortField: sortField.toUpperCase(),
                    sortOrder: sortOrder,
                    searchText: searchFilters.value.searchTerm,
                    dates: searchFilters.value.dates,
                    statuses: searchFilters.value.statuses,
                    highRisk: searchFilters.value.isHighRiskOnly,
                    departments: searchFilters.value.departmentIds,
                    cities: searchFilters.value.cityIds,
                    countries: searchFilters.value.countryIds,
                }, { signal: fetchProgramsAbortController.signal }),
                travelRepository.admin.programsStatsList({
                    searchText: searchFilters.value.searchTerm,
                    dates: searchFilters.value.dates,
                    statuses: searchFilters.value.statuses,
                    highRisk: searchFilters.value.isHighRiskOnly,
                    departments: searchFilters.value.departmentIds,
                    cities: searchFilters.value.cityIds,
                    countries: searchFilters.value.countryIds,
                }, { signal: fetchProgramsAbortController.signal })
            ]);

            programsOverview.value = programsResponse.data;
            programStats.value = statsResponse.data;
            console.log(`Fetched ${programsOverview.value} programs with filters applied`);

        } catch (error: any) {
            // Don't log or throw errors for aborted requests
            if (error.name === 'AbortError' || error.name === 'CanceledError') {
                console.log('fetchPrograms request was cancelled');
                return;
            }
            console.error('Error fetching programs:', error);
            programs.value = []; // Clear programs on error
            programStats.value = null; // Clear stats on error
            throw error;
        } finally {
            // Clear the abort controller after the request completes
            fetchProgramsAbortController = null;
        }
    }

    async function fetchProgramsExport(
        sortField: string = "DATES",
        sortOrder: string = "desc",
    ): Promise<ExportItem> {
        try {
            const response = await travelRepository.admin.programsExportList({
                sortField: sortField.toUpperCase(),
                sortOrder: sortOrder,
                searchText: searchFilters.value.searchTerm,
                dates: searchFilters.value.dates,
                statuses: searchFilters.value.statuses,
                highRisk: searchFilters.value.isHighRiskOnly,
                departments: searchFilters.value.departmentIds,
                cities: searchFilters.value.cityIds,
                countries: searchFilters.value.countryIds
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching program exports', error);
            throw error;
        }
    }

    async function fetchProgramDetails(programId: string | undefined): Promise<TripDetail[]> {
        try {
            const response = await travelRepository.admin.programsDetail(programId ?? '');

            // Assign traveler to trip's traveler property for each item
            const details = response.data ?? [];
            details.forEach(detail => {
                if (detail.trip && detail.traveler) {
                    detail.trip.traveler = detail.traveler;
                }
            });
            selectedProgramDetails.value = details;
            return details;
        } catch (error) {
            console.error('Error fetchingprogram details', error);
            throw error;
        }
    }

    async function fetchProgramDetailForMitId(programId: string, mitId: string): Promise<TripDetail | null> {
        try {
            const response = await travelRepository.admin.programsDetail(programId ?? '');
            const detail = response.data?.find(d => d.trip?.mitId === mitId);
            return detail ?? null;
        } catch (error) {
            console.error('Error fetchingprogram details', error);
            throw error;
        }
    }

    async function addUpdateProgram(program: Program): Promise<void> {
        try {
            if (program.id == null) {
                var response = await travelRepository.programs.programsCreate(program);
                selectedProgram.value = response.data;
            } else {
                await travelRepository.programs.programsUpdate(program);
                selectedProgram.value = program;
            }
            sessionStorage.setItem('selectedProgram', JSON.stringify(selectedProgram.value));
        } catch (error) {
            console.error('Error fetchingprogram details', error);
            throw error;
        }
    }

    async function removeTravelerFromProgram(tripId: string | undefined): Promise<void> {
        try {
            if (!tripId || !selectedProgram.value) {
                return;
            }
            await travelRepository.trips.tripsDelete(tripId ?? '');

        } catch (error) {
            console.error('Error removing traveler from program', error);
            throw error;
        }
    }

    async function addTravelersToProgram(travelers: Traveler[]): Promise<void> {
        try {
            if (!travelers || !selectedProgram.value) {
                return;
            }
            await travelRepository.admin.programsTravelersCreate(selectedProgram.value.id ?? '', { travelers });

        } catch (error) {
            console.error('Error adding travelers to program', error);
            throw error;
        }
    }

    async function fetchProgramTravelersExport(programId: string | undefined): Promise<ExportItem> {
        try {
            const response = await travelRepository.admin.programsTravelersExportList(programId ?? '');

            return response.data;
        } catch (error) {
            console.error('Error fetchingprogram details', error);
            throw error;
        }
    }

    async function cancelProgram(programId: string | undefined): Promise<void> {
        try {
            await travelRepository.programs.programsDelete(programId ?? '');
        } catch (error) {
            console.error('Error fetchingprogram details', error);
            throw error;
        }
    }

    async function updateProgramAdminLeg(): Promise<void> {
        console.log('Updating program admin leg:', selectedAdminTrip.value);
        try {
            if (selectedAdminTrip.value == null) {
                throw new Error('No selected admin trip to update');
            }
            await travelRepository.admin.programsTripsUpdate(selectedProgram.value?.id ?? '', selectedAdminTrip.value);

        } catch (error) {
            console.error('Error fetchingprogram details', error);
            throw error;
        }
    }

    async function addProgramAdminLeg(): Promise<void> {
        console.log('Updating program admin leg:', selectedAdminTrip.value);
        try {
            if (selectedAdminTrip.value == null) {
                throw new Error('No selected admin trip to update');
            }
            await travelRepository.admin.programsTripsCreate(selectedProgram.value?.id ?? '', selectedAdminTrip.value);

        } catch (error) {
            console.error('Error fetchingprogram details', error);
            throw error;
        }
    }

    async function updateProgramAdminTravelersForLeg(): Promise<void> {
        console.log('Updating program admin traveler for leg:', selectedAdminTrip.value);
        try {
            if (selectedAdminTrip.value == null) {
                throw new Error('No selected admin trip to update');
            }
            await travelRepository.admin.programsTripsTravelersCreate(selectedProgram.value?.id ?? '', selectedAdminTrip.value);

        } catch (error) {
            console.error('Error fetchingprogram details', error);
            throw error;
        }
    }

    async function deleteProgramAdminLeg(adminManagedKey: string | undefined): Promise<void> {
        try {
            if (!adminManagedKey || !selectedProgram.value) {
                return;
            }
            const travelerMitIds = (selectedProgram.value?.travelers?.map(t => t.mitId) ?? []).join(',');
            await travelRepository.admin.programsTripsDelete(selectedProgram.value.id ?? '', { adminManagedKey, mitIds: travelerMitIds });
        } catch (error) {
            console.error('Error deleting program admin leg', error);
            throw error;
        }
    }

    async function sendProgramReminders(programId: string, missingPassportMitIds: string[] = []): Promise<void> {
        try {
            await travelRepository.admin.programsNotificationsCreate(programId ?? '', { missingPassportMitIds });
        } catch (error) {
            console.error('Error sending program reminders', error);
            throw error;
        }
    }


    function lookupDepartmentName(departmentId: string): string {
        const department = departments.value.find(dept => dept.id === departmentId);
        return department ? department.name : 'Unknown Department';
    }

    function lookupDepartmentByCode(departmentId: string): Department | undefined {
        const department = departments.value.find(dept => dept.id === departmentId);
        return department;
    }

    // Update the fetchPrograms method to handle filter parameters
    async function fetchTrips(
        pageNumber: number = 1,
        sortField: string = "DATES",
        sortOrder: string = "desc",
    ): Promise<void> {
        try {
            // Cancel the previous request if it's still ongoing
            if (fetchTripsAbortController) {
                fetchTripsAbortController.abort();
                console.log('Previous fetchTrips request cancelled');
            }

            // Create a new AbortController for this request
            fetchTripsAbortController = new AbortController();

            // Run both API calls in parallel
            const [tripsResponse, statsResponse] = await Promise.all([
                travelRepository.admin.tripsList({
                    pageNumber: pageNumber.toString(),
                    pageSize: "20",
                    sortField: sortField,
                    sortOrder: sortOrder,
                    searchText: searchFilters.value.searchTerm,
                    dates: searchFilters.value.dates,
                    statuses: searchFilters.value.statuses,
                    highRisk: searchFilters.value.isHighRiskOnly,
                    tripTypes: searchFilters.value.tripTypes,
                    cities: searchFilters.value.cityIds,
                    countries: searchFilters.value.countryIds,
                    finalDestination: searchFilters.value.isFinalDestination,
                    affiliations: searchFilters.value.affiliations
                }, { signal: fetchTripsAbortController.signal }),
                travelRepository.admin.tripsStatsList({
                    searchText: searchFilters.value.searchTerm,
                    dates: searchFilters.value.dates,
                    statuses: searchFilters.value.statuses,
                    highRisk: searchFilters.value.isHighRiskOnly,
                    tripTypes: searchFilters.value.tripTypes,
                    cities: searchFilters.value.cityIds,
                    countries: searchFilters.value.countryIds,
                    finalDestination: searchFilters.value.isFinalDestination,
                    affiliations: searchFilters.value.affiliations
                }, { signal: fetchTripsAbortController.signal })
            ]);

            tripsOverview.value = tripsResponse.data;
            tripStats.value = statsResponse.data;
            // If you want to do something with statsResponse, add it here
            console.log(`Fetched ${tripsOverview.value?.list?.length} trips with filters applied`);

        } catch (error: any) {
            // Don't log or throw errors for aborted requests
            if (error.name === 'AbortError' || error.name === 'CanceledError') {
                console.log('fetchTrips request was cancelled');
                return;
            }
            console.error('Error fetching trips:', error);
            trips.value = []; // Clear trips on error
            throw error;
        } finally {
            // Clear the abort controller after the request completes
            fetchTripsAbortController = null;
        }
    }

    // Update the fetchPrograms method to handle filter parameters
    async function fetchTravelerTrips(
        travelerMitId?: string,
        pageNumber: number = 1,
        sortFielfd: string = "DATES",
        sortOrder: string = "desc"
    ): Promise<Trip[]> {
        try {
            // Cancel the previous request if it's still ongoing
            if (fetchTravelerTripsAbortController) {
                fetchTravelerTripsAbortController.abort();
                console.log('Previous fetchTravelerTrips request cancelled');
            }

            // Create a new AbortController for this request
            fetchTravelerTripsAbortController = new AbortController();

            const response = await travelRepository.admin.tripsList({
                pageNumber: pageNumber.toString(),
                pageSize: "20",
                sortField: sortFielfd,
                sortOrder: sortOrder,
                searchText: searchFilters.value.searchTerm,
                dates: searchFilters.value.dates,
                statuses: searchFilters.value.statuses,
                highRisk: searchFilters.value.isHighRiskOnly,
                tripTypes: searchFilters.value.tripTypes,
                cities: searchFilters.value.cityIds,
                countries: searchFilters.value.countryIds,
                travelerMitId: travelerMitId,
                finalDestination: searchFilters.value.isFinalDestination,
            });

            return response.data.list ?? [];

        } catch (error) {
            console.error('Error fetching programs:', error);
            trips.value = []; // Clear trips on error
            throw error;
        } finally {
            // Clear the abort controller after the request completes
            fetchTravelerTripsAbortController = null;
        }
    }

    async function fetchTripsExport(
        sortField: string = "DATES",
        sortOrder: string = "desc",
    ): Promise<ExportItem> {
        try {
            const response = await travelRepository.admin.tripsExportList({
                sortField: sortField.toUpperCase(),
                sortOrder: sortOrder,
                searchText: searchFilters.value.searchTerm,
                tripTypes: searchFilters.value.tripTypes,
                dates: searchFilters.value.dates,
                statuses: searchFilters.value.statuses,
                highRisk: searchFilters.value.isHighRiskOnly,
                cities: searchFilters.value.cityIds,
                countries: searchFilters.value.countryIds,
                finalDestination: searchFilters.value.isFinalDestination,
                affiliations: searchFilters.value.affiliations
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching trip exports', error);
            throw error;
        }
    }

    async function updateTrip(): Promise<Trip | undefined> {
        console.log('Updating trip:', selectedTrip);
        try {
            if (selectedTrip.value == null) {
                console.error('selectedTrip is null');
                return undefined;
            }

            if (selectedTrip.value.id == null) {
                const response = await travelRepository.admin.tripsCreate(selectedTrip.value);

                response.data.segments = response.data.segments?.map(seg => ({ ...seg, tripId: response.data.id ?? '' }));

                selectedTrip.value = response.data;
            }
            await travelRepository.admin.tripsUpdate(selectedTrip.value);

            return selectedTrip.value
        } catch (error) {
            console.error('Error updating trip', error);
            throw error;
        }
    }

    async function updateTravelerContact(contact: TravelerContact & ProgramContact): Promise<TravelerContact & ProgramContact | undefined> {
        console.log('Updating trip contact:', selectedTrip);
        try {
            if (selectedTrip.value == null) {
                console.error('selectedTrip is null');
                return undefined;
            }

            if (contact.id == null) {
                const response = await travelRepository.admin.contactsCreate(contact);
                return response.data as TravelerContact & ProgramContact
            } else {
                await travelRepository.admin.contactsUpdate(contact);
                return contact
            }
        } catch (error) {
            console.error('Error updating trip traveler', error);
            throw error;
        }
    }

    async function deleteTrip(): Promise<void> {
        console.log('Cancelling trip:', selectedTrip);
        try {
            if (selectedTrip.value == null || selectedTrip.value.id === '') {
                console.error('selectedTrip is null');
                return undefined;
            }
            await travelRepository.admin.tripsDelete(selectedTrip.value.id!, { mitId: selectedTrip.value.mitId ?? '' });
            selectedTrip.value = null;
            sessionStorage.removeItem('selectedTrip');
        } catch (error) {
            console.error('Error updating trip', error);
            throw error;
        }
    }

    async function fetchTrip(tripId: string): Promise<Trip | undefined> {
        try {
            const response = await travelRepository.admin.tripsDetail(tripId);
            return response.data;
        } catch (error) {
            console.error('Error fetching trip', error);
            throw error;
        }
    }

    function setSelectedProgram(program: Program) {
        selectedProgram.value = program;
    }

    function setSelectedTrip(trip: Trip) {
        selectedTrip.value = trip;
    }

    async function fetchDepartments(): Promise<void> {
        if (departments.value.length > 0) {
            return;
        }
        try {
            // Call the API with the filter parameters
            const response = await travelRepository.departments.departmentsList({ all: adminRole.value?.superAdmin });
            departments.value = response.data.departments ?? [];
        } catch (error) {
            console.error('Error fetching programs:', error);
            departments.value = []; // Clear programs on error 
            throw error;
        }
    }

    async function searchDepartments(query: string): Promise<Department[]> {
        let filteredDepartments: Department[] = [];
        departments.value.forEach(element => {
            if (element.name.toLowerCase().includes(query.toLowerCase()) || element.id.toLowerCase().includes(query.toLowerCase())) {
                filteredDepartments.push(element);
            }
        });
        return filteredDepartments;
    }

    let countriesPromise: Promise<void> | null = null;

    async function fetchCountries(): Promise<void> {
        if (allCountries.value.length > 0) {
            return;
        }
        if (countriesPromise) {
            return countriesPromise;
        }
        countriesPromise = (async () => {
            try {
                // Call the API with the filter parameters
                const response = await travelRepository.countries.countriesList();
                highRiskCountries.value = response.data.countries?.filter(c => c.highRisk) ?? [];
                allCountries.value = response.data.countries ?? [];
            } catch (error) {
                console.error('Error fetching programs:', error);
                highRiskCountries.value = []; // Clear programs on error
                throw error;
            } finally {
                countriesPromise = null;
            }
        })();
        return countriesPromise;
    }

    async function fetchStates(): Promise<void> {
        if (states.value.length > 0) {
            return;
        }

        try {
            const response = await travelRepository.states.statesList();
            states.value = response.data.states ?? [];
        } catch (error) {
            console.error('Error fetching states:', error);
            states.value = []; // Clear states on error
            throw error;
        }
    }

    async function updateHighRiskCountry(country: Country): Promise<void> {
        try {
            const response = await travelRepository.countries.countriesUpdate(country);
            if (!country.highRisk) {
                highRiskCountries.value = highRiskCountries.value.filter(c => c.code !== country.code);
            } else {
                const index = highRiskCountries.value.findIndex(c => c.code === country.code);
                if (index !== -1) {
                    highRiskCountries.value[index] = country;
                } else {
                    highRiskCountries.value.push(country);
                }
            }
        } catch (error) {
            console.error('Error deleting high risk country:', error);
            throw error;
        }
    }

    let transportModesPromise: Promise<void> | null = null;

    async function fetchTransportModes(): Promise<void> {
        if (transportModes.value.length > 0) {
            return;
        }
        if (transportModesPromise) {
            return transportModesPromise;
        }
        transportModesPromise = (async () => {
            try {
                // Call the API with the filter parameters
                const response = await travelRepository.transportModes.transportModesList();
                transportModes.value = response.data.transports ?? [];
            } catch (error) {
                console.error('Error fetching transport modes:', error);
                departments.value = []; // Clear programs on error 
                throw error;
            } finally {
                transportModesPromise = null;
            }
        })();
        return transportModesPromise;
    }

    async function searchAirlines(search: string): Promise<Airline[]> {
        try {
            // Call the API with the filter parameters
            const response = await travelRepository.airlines.airlinesList({ q: search });
            return response.data.airlines ?? [];
        } catch (error) {
            console.error('Error fetching airlines:', error);
            throw error;
        }
    }

    async function searchAirports(search: string): Promise<Airport[]> {
        try {
            if (search.length > 3 &&
                ('johannesburg'.startsWith(search.toLowerCase()) ||
                    search.toLowerCase() == 'jnb')) {
                search = "Jo'anna";
            } else if (search.length > 3 &&
                'or tambo'.startsWith(search.toLowerCase())) {
                search = 'tambo';
            }
            // Call the API with the filter parameters
            const response = await travelRepository.airports.aeroboxList({ search: search, maxResults: 6 });
            return response.data ?? [];
        } catch (error) {
            console.error('Error fetching airlines:', error);
            throw error;
        }
    }

    async function fetchAirport(code: string): Promise<Airport | undefined> {
        try {
            if (code.toLowerCase().includes('tambo')) {
                code = "Jo'anna";
            }
            const response = await travelRepository.airports.aeroboxList({ search: code, maxResults: 1 });
            return response.data && response.data.length > 0 ? response.data[0] : undefined;
        } catch (error) {
            console.error('Error fetching airlines:', error);
            throw error;
        }
    }

    async function searchFlights(flightNumber: string, date: string): Promise<Flight[]> {
        try {
            const response = await travelRepository.flights.flightsList({ flightNumber, date });
            return response.data ?? [];
        } catch (error) {
            console.error('Error searching flights:', error);
            throw error;
        }
    }

    async function clearFinalDestinationForAdminLeg(adminManagedKey: string): Promise<void> {
        const segment = (() => {
            for (const detail of selectedProgramDetails.value) {
                const seg = detail.trip?.segments?.find(s => s.adminManagedKey === adminManagedKey);
                if (seg) return seg;
            }
            return null;
        })();
        if (!segment || !selectedProgram.value?.id) return;
        const travelers = selectedProgramDetails.value
            .filter(d => d.trip?.segments?.some(s => s.adminManagedKey === adminManagedKey))
            .map(d => d.traveler?.mitId)
            .filter((id): id is string => !!id);
        await travelRepository.admin.programsTripsUpdate(selectedProgram.value.id, {
            tripLeg: { ...segment, finalDestination: false },
            travelers
        });
    }

    function clearFilterStorage() {
        sessionStorage.removeItem('affiliations');
        sessionStorage.removeItem('tripTypes');
        sessionStorage.removeItem('statuses');
        sessionStorage.removeItem('countries');
        sessionStorage.removeItem('places');
        sessionStorage.removeItem('cities');
        sessionStorage.removeItem('departments');
        sessionStorage.removeItem('dates');
        sessionStorage.removeItem('isHighRisk');
        searchFilters.value = {
            searchTerm: undefined,
            tripTypes: undefined,
            countryIds: undefined,
            cityIds: undefined,
            departmentIds: undefined,
            statuses: undefined,
            dates: undefined,
            isHighRiskOnly: undefined
        };
    };

    return {
        clearFilterStorage,
        hasAccess,
        searchFilters,
        fetchRole,
        adminRole,
        programsOverview,
        fetchPrograms,
        fetchProgramDetails,
        fetchProgramDetailForMitId,
        fetchProgramsExport,
        selectedProgramDetails,
        selectedProgram,
        programStats,
        setSelectedProgram,
        addUpdateProgram,
        cancelProgram,
        removeTravelerFromProgram,
        addTravelersToProgram,
        deleteProgramAdminLeg,
        fetchProgramTravelersExport,
        addProgramAdminLeg,
        updateProgramAdminLeg,
        updateProgramAdminTravelersForLeg,
        sendProgramReminders,
        fetchTravelerProfile,
        fetchTravelerTrips,
        fetchTripsExport,
        lookupDepartmentName,
        isTripsInitialised,
        trips,
        tripsOverview,
        fetchTrips,
        selectedTrip,
        updateTrip,
        fetchTrip,
        deleteTrip,
        tripStats,
        setSelectedTrip,
        fetchTripTravelerContact,
        fetchTripEmergencyContact,
        updateTravelerContact,
        departments,
        fetchDepartments,
        searchDepartments,
        lookupDepartmentByCode,
        fetchCountries,
        updateHighRiskCountry,
        highRiskCountries,
        allCountries,
        states,
        fetchStates,
        selectedTravelerProfile,
        fetchTransportModes,
        transportModes,
        selectedAdminTrip,
        searchAirlines,
        searchAirports,
        fetchAirport,
        searchFlights,
        clearFinalDestinationForAdminLeg,
        pendingLegKeys,
        optimisticNewLegs
    };

});